import { FunctionComponent, useEffect, useState } from "react";
import { api } from "../../api";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, Button, Stack, Typography } from "@mui/material";

import { getENV } from "../../../utils/getEnv";
import { constants } from "../../../utils/constants";
import { BulkAddStepProps } from "../BulkAddDialogTypes";
import { downloadFile } from "../../../utils/downloadFile";
import { ImportRowActionsStatsResponse } from "@akitabox/api-client";

export const ShowResults: FunctionComponent<BulkAddStepProps> = ({
  importId,
}) => {
  const [percentErrored, setPercentErrored] = useState<number>(0);
  // percentCompleted is fetched but not used directly in UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [completedRows, setCompletedRows] = useState<number>(0);
  const [erroredRows, setErroredRows] = useState<number>(0);

  async function fetchImportStats(
    importId: string
  ): Promise<ImportRowActionsStatsResponse | null> {
    try {
      const { data: response } = await api.imports.importRowActionsStats({
        importId,
      });
      return response.data;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    let ignore = false;
    if (importId) {
      fetchImportStats(importId).then((stats) => {
        if (!ignore && stats) {
          setPercentErrored(stats.percent_errored ?? 0);
          setCompletedRows(stats.number_completed ?? 0);
          setErroredRows(stats.number_errored ?? 0);
        }
      });
    }
    return () => {
      ignore = true;
    };
  }, [importId]);
  const handleDownloadResults = () => {
    if (!importId) return;
    const env = getENV();
    const { URL_BASE } = constants.APPS.CORE.ENV[env];
    const apiURL = URL_BASE.replace(/:\d+$/, "");
    const path = `/my_imports/${importId}/rows_csv`;
    const csvImportRowsURL = new URL(path, apiURL);
    downloadFile(csvImportRowsURL);
  };

  const handleDownloadErrors = () => {
    if (!importId || percentErrored <= 0) return;
    const env = getENV();
    const { URL_BASE } = constants.APPS.CORE.ENV[env];
    const apiURL = URL_BASE.replace(/:\d+$/, "");
    const path = `/my_imports/${importId}/rows_csv`;
    const erroredParams = new URLSearchParams({ status: "errored" });
    const csvImportRowsURL = new URL(path, apiURL);
    csvImportRowsURL.search = erroredParams.toString();
    downloadFile(csvImportRowsURL);
  };

  return (
    <Box
      sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 2 }}
    >
      <Box
        sx={{
          maxWidth: 400,
          width: "100%",
          p: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start", // left-justify content
        }}
      >
        <Typography fontWeight="bold" gutterBottom align="left">
          Import Results
        </Typography>
        <Typography gutterBottom align="left">
          Download the full results of your import or just the rows that
          encountered errors.
        </Typography>
        <Box sx={{ mt: 2, width: "100%" }}>
          <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}>
            {completedRows} row{completedRows === 1 ? "" : "s"} imported
            successfully
          </Typography>
          <Typography
            variant="body2"
            color={erroredRows > 0 ? "error" : "text.secondary"}
          >
            {erroredRows} row{erroredRows === 1 ? "" : "s"} failed to import
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2 }}
          alignItems="flex-start"
        >
          <Button
            color="primary"
            variant="contained"
            onClick={handleDownloadResults}
            endIcon={<FileDownloadIcon />}
            size="small"
          >
            Download Results CSV
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleDownloadErrors}
            endIcon={percentErrored > 0 ? <FileDownloadIcon /> : null}
            size="small"
            disabled={percentErrored <= 0}
          >
            {percentErrored > 0 ? "Download Errors CSV" : "No Errors"}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
